import React, { useEffect, useRef, useState } from "react";
import {
  Loader2, X, Upload, ArrowRight, AlertCircle, FileText, Sparkles, Plus, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

const SEV_PILL = {
  critical: "bg-rose-100 text-rose-700",
  high:     "bg-orange-100 text-orange-700",
  medium:   "bg-amber-100 text-amber-700",
  low:      "bg-emerald-100 text-emerald-700",
};

const LEVEL_PILL = {
  critical: "bg-rose-50 text-rose-700 ring-rose-200",
  high:     "bg-orange-50 text-orange-700 ring-orange-200",
  medium:   "bg-amber-50 text-amber-700 ring-amber-200",
  low:      "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

/**
 * VersionsModal — modal that shows a document's version history, lets the
 * owner upload a new version, and diffs two selected versions
 * (risk-finding diff + raw text diff).
 */
export default function VersionsModal({ doc, onClose, onReload }) {
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffing, setDiffing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}/versions`);
      setVersions(data.versions || []);
      // Default diff selection: previous → current. Picks the latest two
      // by version_number for the easiest "what changed?" answer.
      const sorted = [...(data.versions || [])].sort((a, b) => a.version_number - b.version_number);
      if (sorted.length >= 2) {
        setFromId(sorted[sorted.length - 2].doc_id);
        setToId(sorted[sorted.length - 1].doc_id);
      } else if (sorted.length === 1) {
        setFromId(sorted[0].doc_id);
        setToId(sorted[0].doc_id);
      }
    } catch (err) {
      toast.error("Couldn't load versions", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVersions(); }, [doc.id]);  // eslint-disable-line

  // Auto-fetch diff whenever both selectors are valid + distinct.
  useEffect(() => {
    if (!fromId || !toId || fromId === toId) {
      setDiff(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDiffing(true);
      try {
        const { data } = await api.get(
          `/documents/diff?from_id=${fromId}&to_id=${toId}`,
        );
        if (!cancelled) setDiff(data);
      } catch (err) {
        if (!cancelled) toast.error("Diff failed", { description: formatApiError(err) });
      } finally {
        if (!cancelled) setDiffing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fromId, toId]);

  const uploadNewVersion = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/documents/${doc.id}/versions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Saved as v${data.version_number}`, {
        description: "Scan it to compare risks against earlier versions.",
      });
      fileRef.current.value = "";
      await fetchVersions();
      if (typeof onReload === "function") onReload();
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 px-2"
      onClick={onClose}
      data-testid="versions-modal"
    >
      <div
        className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
              Version history
            </div>
            <h2 className="font-display text-lg font-semibold text-slate-900 mt-1 leading-tight truncate">
              {doc.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900"
            aria-label="Close"
            data-testid="versions-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {/* Upload row -------------------------------------------------- */}
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
            <div className="text-xs font-semibold text-slate-900 mb-2">
              Upload a new version
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.txt,.md"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="versions-file-input"
              />
              <Button
                onClick={uploadNewVersion}
                disabled={busy}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                data-testid="versions-upload"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                Upload
              </Button>
            </div>
          </div>

          {/* Version list ------------------------------------------------ */}
          {loading ? (
            <div className="py-8 flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading versions…
            </div>
          ) : (
            <VersionTimeline
              versions={versions}
              fromId={fromId} toId={toId}
              setFromId={setFromId} setToId={setToId}
            />
          )}

          {/* Diff -------------------------------------------------------- */}
          {versions.length >= 2 && (
            <DiffPane
              diff={diff}
              diffing={diffing}
              fromId={fromId} toId={toId}
              setFromId={setFromId} setToId={setToId}
              versions={versions}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function VersionTimeline({ versions, fromId, toId, setFromId, setToId }) {
  if (!versions || versions.length === 0) return null;
  const sorted = [...versions].sort((a, b) => a.version_number - b.version_number);
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="versions-timeline">
      <ul className="divide-y divide-slate-100">
        {sorted.map((v) => {
          const isFrom = v.doc_id === fromId;
          const isTo = v.doc_id === toId;
          return (
            <li
              key={v.doc_id}
              className={`px-4 py-3 flex items-center gap-3 ${v.is_current ? "bg-blue-50/40" : ""}`}
              data-testid={`version-row-v${v.version_number}`}
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 text-xs font-bold shrink-0">
                v{v.version_number}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                  {v.name}
                  {v.is_current ? (
                    <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 font-semibold px-1.5 py-0.5 rounded">
                      Open
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-3">
                  <span>{v.created_by_name || "—"}</span>
                  <span>{new Date(v.created_at).toLocaleString()}</span>
                  <span>{v.char_count.toLocaleString()} chars</span>
                  {v.last_scan_id ? (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${LEVEL_PILL[v.last_scan_level] || LEVEL_PILL.medium}`}>
                      {v.last_scan_level} · {v.last_scan_score}
                    </span>
                  ) : (
                    <span className="text-slate-400">Not scanned</span>
                  )}
                </div>
              </div>
              {versions.length >= 2 && (
                <div className="flex flex-col items-end gap-1 shrink-0 text-[10px] uppercase tracking-wider font-semibold">
                  <button
                    type="button"
                    onClick={() => setFromId(v.doc_id)}
                    className={`px-2 py-0.5 rounded ${isFrom ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                    data-testid={`version-pick-from-v${v.version_number}`}
                  >
                    Before
                  </button>
                  <button
                    type="button"
                    onClick={() => setToId(v.doc_id)}
                    className={`px-2 py-0.5 rounded ${isTo ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"}`}
                    data-testid={`version-pick-to-v${v.version_number}`}
                  >
                    After
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DiffPane({ diff, diffing, fromId, toId, versions }) {
  if (!fromId || !toId) return null;
  if (fromId === toId) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 inline-flex items-center gap-2">
        <AlertCircle className="h-4 w-4" /> Pick a <strong>Before</strong> and an <strong>After</strong> version above to see what changed.
      </div>
    );
  }
  if (diffing) {
    return (
      <div className="py-8 flex items-center justify-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Comparing versions…
      </div>
    );
  }
  if (!diff) return null;

  const rs = diff.risk_summary || {};
  const scoreDelta = (rs.to_score ?? 0) - (rs.from_score ?? 0);
  const issueDelta = (rs.to_issue_count ?? 0) - (rs.from_issue_count ?? 0);

  return (
    <div className="space-y-4" data-testid="versions-diff">
      {/* Risk summary headline */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1.5 mb-2">
          <Sparkles className="h-3 w-3" /> Risk diff
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            label="Score change"
            value={rs.from_score == null && rs.to_score == null ? "—" :
              `${rs.from_score ?? "?"} → ${rs.to_score ?? "?"}`}
            delta={scoreDelta}
            higherIsBetter={true}
          />
          <Stat
            label="Issues"
            value={`${rs.from_issue_count ?? 0} → ${rs.to_issue_count ?? 0}`}
            delta={issueDelta}
            higherIsBetter={false}
          />
          <Stat label="New" value={rs.new_count ?? 0} tone="rose" />
          <Stat label="Resolved" value={rs.resolved_count ?? 0} tone="emerald" />
        </div>
      </div>

      {/* Per-issue diff buckets */}
      {(diff.risk_diff?.new?.length ?? 0) > 0 && (
        <DiffBucket
          title="New issues introduced"
          icon={Plus}
          tone="rose"
          items={diff.risk_diff.new}
          renderItem={(i) => ({ title: i.title, severity: i.severity, detail: i.detail })}
        />
      )}
      {(diff.risk_diff?.resolved?.length ?? 0) > 0 && (
        <DiffBucket
          title="Issues resolved"
          icon={Minus}
          tone="emerald"
          items={diff.risk_diff.resolved}
          renderItem={(i) => ({ title: i.title, severity: i.severity, detail: i.detail })}
        />
      )}
      {(diff.risk_diff?.changed?.length ?? 0) > 0 && (
        <DiffBucket
          title="Severity changes"
          icon={ArrowRight}
          tone="amber"
          items={diff.risk_diff.changed}
          renderItem={(i) => ({
            title: i.title,
            subtitle: `${i.from_severity} → ${i.to_severity}`,
            detail: i.detail,
            severity: i.to_severity,
          })}
        />
      )}
      {!(diff.risk_diff?.new?.length || diff.risk_diff?.resolved?.length || diff.risk_diff?.changed?.length) && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <strong className="text-slate-900">No risk-level changes detected.</strong> Either both versions were scanned identically, or one side wasn't scanned yet.
        </div>
      )}

      {/* Text diff */}
      <details className="rounded-2xl border border-slate-200 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-500" /> Show text diff
          {diff.text_diff_truncated ? (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              truncated
            </span>
          ) : null}
        </summary>
        <div className="border-t border-slate-100 p-4 bg-slate-50 max-h-72 overflow-y-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words">
          {(diff.text_diff || []).map((hunk, idx) => {
            if (hunk.tag === "equal") {
              return (
                <div key={idx} className="text-slate-500">
                  {hunk.lines.map((l, i) => <div key={i}>  {l || "\u00a0"}</div>)}
                </div>
              );
            }
            if (hunk.tag === "ellipsis") {
              return (
                <div key={idx} className="text-slate-400 italic">⋯ {hunk.skipped} unchanged lines</div>
              );
            }
            if (hunk.tag === "delete") {
              return (
                <div key={idx} className="text-rose-700 bg-rose-50/40">
                  {hunk.lines.map((l, i) => <div key={i}>- {l || "\u00a0"}</div>)}
                </div>
              );
            }
            if (hunk.tag === "insert") {
              return (
                <div key={idx} className="text-emerald-700 bg-emerald-50/40">
                  {hunk.lines.map((l, i) => <div key={i}>+ {l || "\u00a0"}</div>)}
                </div>
              );
            }
            return null;
          })}
        </div>
      </details>
    </div>
  );
}

function Stat({ label, value, delta, higherIsBetter, tone }) {
  let valueClass = "text-slate-900";
  let deltaText = null;
  if (typeof delta === "number" && delta !== 0) {
    const goodWay = higherIsBetter ? delta > 0 : delta < 0;
    deltaText = (
      <span className={`text-xs font-semibold ${goodWay ? "text-emerald-700" : "text-rose-700"}`}>
        {delta > 0 ? "+" : ""}{delta}
      </span>
    );
  }
  if (tone === "rose") valueClass = "text-rose-700";
  if (tone === "emerald") valueClass = "text-emerald-700";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-lg font-bold tabular-nums mt-0.5 ${valueClass}`}>{value}</div>
      {deltaText}
    </div>
  );
}

function DiffBucket({ title, icon: Icon, tone, items, renderItem }) {
  const tones = {
    rose:    "border-rose-200 bg-rose-50/50",
    emerald: "border-emerald-200 bg-emerald-50/50",
    amber:   "border-amber-200 bg-amber-50/50",
  };
  return (
    <div className={`rounded-2xl border ${tones[tone]} p-4`}>
      <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" /> {title} ({items.length})
      </div>
      <ul className="space-y-2">
        {items.map((raw, idx) => {
          const r = renderItem(raw);
          return (
            <li key={idx} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
              <div className="flex items-start gap-2">
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${SEV_PILL[r.severity] || SEV_PILL.medium}`}>
                  {r.severity || "medium"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{r.title}</div>
                  {r.subtitle && (
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mt-0.5">
                      {r.subtitle}
                    </div>
                  )}
                  {r.detail && (
                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{r.detail}</div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
